import type { ActiveHintStage } from "@/game/feedback";
import type {
  SequenceHint,
  SequencePayload
} from "@/game/templates/sequence";

import { defineCurriculumVoiceCue, type CurriculumVoiceCue } from "./cue";

const DAILY_ROUTINE_REDUCED_CHOICES_CUE = defineCurriculumVoiceCue(
  "/audio/habits/feedback/daily-routine-reduced-choices.ogg",
  "只看亮起来的两张卡片。哪一步先来？"
);

function getSequenceItemLabel(
  payload: SequencePayload,
  itemId: string
): string {
  const item = payload.items.find(({ id }) => id === itemId);

  if (item === undefined) {
    throw new Error("Routine order references missing item " + itemId);
  }

  return item.label;
}

export function getDailyRoutineFeedbackCue(
  levelId: string,
  payload: SequencePayload,
  feedbackKind: "hint" | "retry",
  hint: SequenceHint
): CurriculumVoiceCue {
  const pathPrefix = "/audio/habits/feedback/" + levelId;

  switch (hint.stage) {
    case "gentle-hint":
      return defineCurriculumVoiceCue(
        pathPrefix + "-" + feedbackKind + "-gentle-hint.ogg",
        feedbackKind === "retry"
          ? "卡片还有一点没排好。先找到最早发生的那一步。"
          : "哪一步最先发生？先把它请到前面。"
      );
    case "reduced-choices":
      return DAILY_ROUTINE_REDUCED_CHOICES_CUE;
    case "replay-demonstration":
      return defineCurriculumVoiceCue(
        pathPrefix + "-replay-demonstration.ogg",
        "点点陪你顺着读：" +
          hint.order
            .map((itemId) => getSequenceItemLabel(payload, itemId))
            .join("，接着") +
          "。"
      );
  }
}

const SCHOOLBAG_HINT_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = {
  "gentle-hint": defineCurriculumVoiceCue(
    "/audio/habits/feedback/schoolbag-hint-gentle-hint.ogg",
    "想想它明天会不会用到，再帮它选个位置。"
  ),
  "reduced-choices": defineCurriculumVoiceCue(
    "/audio/habits/feedback/schoolbag-hint-reduced-choices.ogg",
    "书包还是收纳篮？想想它明天要不要一起上学。"
  ),
  "replay-demonstration": defineCurriculumVoiceCue(
    "/audio/habits/feedback/schoolbag-hint-replay-demonstration.ogg",
    "看看它是做什么的，再送它去书包或收纳篮。"
  )
};

const SCHOOLBAG_RETRY_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = {
  "gentle-hint": defineCurriculumVoiceCue(
    "/audio/habits/feedback/schoolbag-retry-gentle-hint.ogg",
    "这个小物品想换个位置。明天会用到它吗？"
  ),
  "reduced-choices": defineCurriculumVoiceCue(
    "/audio/habits/feedback/schoolbag-retry-reduced-choices.ogg",
    "再比比书包和收纳篮，哪一个才是它明天的家？"
  ),
  "replay-demonstration": defineCurriculumVoiceCue(
    "/audio/habits/feedback/schoolbag-retry-replay-demonstration.ogg",
    "看看它的用途，再帮它换到合适的位置。"
  )
};

export const SCHOOLBAG_PROGRESS_CUE = defineCurriculumVoiceCue(
  "/audio/habits/feedback/schoolbag-progress.ogg",
  "放对地方啦，接着看看桌上还有谁。"
);

export function getSchoolbagFeedbackCue(
  kind: "hint" | "retry",
  stage: ActiveHintStage
): CurriculumVoiceCue {
  return kind === "hint"
    ? SCHOOLBAG_HINT_CUES[stage]
    : SCHOOLBAG_RETRY_CUES[stage];
}

export const SCHOOLBAG_FEEDBACK_CUES = [
  ...Object.values(SCHOOLBAG_HINT_CUES),
  ...Object.values(SCHOOLBAG_RETRY_CUES),
  SCHOOLBAG_PROGRESS_CUE
] as const;
